import {
  computed,
  inject,
  Injectable,
} from '@angular/core';
import {
  ComponentStore,
} from '@ngrx/component-store';
import { firstValueFrom } from 'rxjs';

import { AlertService } from '../../../core/services/alert/alert.service';
import { resolveApiError } from '../../../core/services/api/api-error.util';
import { TokenService } from '../../../core/services/token/token.service';
import { AuthApiService } from '../data-access/auth-api.service';
import type {
  ChangePasswordPayload,
  MeResponse,
  ObtainTokensPayload,
} from '../models/auth.models';

interface AuthState {
  readonly account: MeResponse | null;
  readonly initialized: boolean;
  readonly loading: boolean;
  readonly actionLoading: boolean;
  readonly error: string | null;
}

const INITIAL_STATE: AuthState = {
  account: null,
  initialized: false,
  loading: false,
  actionLoading: false,
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class AuthStore extends ComponentStore<AuthState> {
  private readonly api =
    inject(AuthApiService);

  private readonly token =
    inject(TokenService);

  private readonly alert =
    inject(AlertService);

  readonly account =
    this.selectSignal(
      (state) => state.account,
    );

  readonly initialized =
    this.selectSignal(
      (state) => state.initialized,
    );

  readonly loading =
    this.selectSignal(
      (state) => state.loading,
    );

  readonly actionLoading =
    this.selectSignal(
      (state) =>
        state.actionLoading,
    );

  readonly error =
    this.selectSignal(
      (state) => state.error,
    );

  readonly role = computed(
    () =>
      this.account()?.role ??
      this.token.accountRole(),
  );

  readonly isAuthenticated =
    computed(() =>
      this.token.isAuthenticated(),
    );

  readonly displayName =
    computed(() => {
      const account =
        this.account();

      if (!account) {
        return '';
      }

      const fullname =
        account.fullname.trim();

      return (
        fullname ||
        account.username
      );
    });

  readonly isAdmin = computed(
    () =>
      this.role() === 'admin',
  );

  readonly isStaff = computed(
    () =>
      this.role() === 'staff',
  );

  readonly isPartner = computed(
    () =>
      this.role() ===
      'partner',
  );

  readonly isClient = computed(
    () =>
      this.role() ===
      'client',
  );

  constructor() {
    super(INITIAL_STATE);
  }

  async restoreSession(): Promise<void> {
    if (this.initialized()) {
      return;
    }

    if (!this.token.hasTokens()) {
      this.patchState({
        initialized: true,
      });
      return;
    }

    this.patchState({
      loading: true,
      error: null,
    });

    try {
      const account =
        await firstValueFrom(
          this.api.me(),
        );

      this.token.setAccountRole(
        account.role,
      );

      this.patchState({
        account,
      });
    } catch {
      this.token.clearTokens();

      this.patchState({
        account: null,
      });
    } finally {
      this.patchState({
        initialized: true,
        loading: false,
      });
    }
  }

  async login(
    payload: ObtainTokensPayload,
  ): Promise<MeResponse | null> {
    if (this.actionLoading()) {
      return null;
    }

    this.token.clearTokens();

    this.patchState({
      account: null,
      actionLoading: true,
      error: null,
    });

    try {
      const tokens =
        await firstValueFrom(
          this.api.obtainTokens({
            username:
              payload.username.trim(),
            password:
              payload.password,
          }),
        );

      this.token.setTokens(
        tokens,
      );

      const account =
        await firstValueFrom(
          this.api.me(),
        );

      this.token.setAccountRole(
        account.role,
      );

      this.patchState({
        account,
        initialized: true,
      });

      return account;
    } catch (error) {
      this.token.clearTokens();

      this.patchState({
        account: null,
        error:
          this.resolveLoginError(
            error,
          ),
      });

      return null;
    } finally {
      this.patchState({
        actionLoading: false,
      });
    }
  }

  async updatePhoto(photo: string | null): Promise<boolean> {
    const current = this.account();
    if (!current || this.actionLoading()) {
      return false;
    }

    this.patchState({
      actionLoading: true,
      error: null,
    });

    try {
      const updated = await firstValueFrom(this.api.updatePhoto(photo));
      this.patchState({
        account: {
          ...current,
          ...updated,
          photo: updated.photo ?? photo,
        },
      });
      return true;
    } catch (error) {
      this.patchState({
        error: resolveApiError(error, 'Не удалось сохранить фото').detail,
      });
      return false;
    } finally {
      this.patchState({
        actionLoading: false,
      });
    }
  }

  async changePassword(
    payload: ChangePasswordPayload,
  ): Promise<boolean> {
    if (this.actionLoading()) {
      return false;
    }

    this.patchState({
      actionLoading: true,
    });

    try {
      await firstValueFrom(
        this.api.changePassword(
          payload,
        ),
      );

      this.alert.success(
        'Пароль успешно изменён.',
      );

      return true;
    } catch (error) {
      this.alert.error(
        this.resolveChangePasswordError(
          error,
        ),
      );

      return false;
    } finally {
      this.patchState({
        actionLoading: false,
      });
    }
  }

  logout(): void {
    this.token.clearTokens();

    this.setState({
      ...INITIAL_STATE,
      initialized: true,
    });
  }

  clearError(): void {
    this.patchState({
      error: null,
    });
  }

  setError(
    message: string,
  ): void {
    this.patchState({
      error: message,
    });
  }

  private resolveLoginError(
    error: unknown,
  ): string {
    const apiError =
      resolveApiError(
        error,
        'Не удалось выполнить вход.',
      );

    switch (
      apiError.detailCode
    ) {
      case 'invalid_creds':
        return 'Неверный логин или пароль.';

      case 'inactive_account':
        return 'Аккаунт деактивирован.';

      default:
        if (
          apiError.status === 422
        ) {
          return 'Проверьте введённые данные.';
        }

        return apiError.detail;
    }
  }

  private resolveChangePasswordError(
    error: unknown,
  ): string {
    const apiError =
      resolveApiError(
        error,
        'Не удалось изменить пароль.',
      );

    switch (
      apiError.detailCode
    ) {
      case 'invalid_creds':
        return 'Текущий пароль указан неверно.';

      case 'password_not_configured':
        return 'Для аккаунта пароль не настроен.';

      case 'inactive_account':
        return 'Аккаунт деактивирован.';

      default:
        return apiError.detail;
    }
  }
}
